import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ORDER_PAYMENT_TERMS_LABELS_RU,
  OrderPaymentTerms,
  needsRopApproval,
  type OrderDetailDto,
  type OrderPaymentTerms as OrderPaymentTermsT,
} from '@crm/shared'
import { updatePaymentTerms } from '../../../shared/api/orders.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { toast } from '../../../shared/ui/toastStore'

/**
 * Условия оплаты заказа (решение Сергея 12.10.2026). Заказ создаётся отметкой
 * «Счёт» с предоплатой 100 % — здесь менеджер ставит настоящие условия. Плановая
 * предоплата меньше 50 % отправляет заказ на согласование РОП; шаблон счёта для
 * УНФ скачивается и до согласования — оно касается закупки, а не счёта (14.10.2026).
 */
export function PaymentTermsModal({
  order,
  onClose,
}: {
  order: OrderDetailDto
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [type, setType] = useState<OrderPaymentTermsT>(order.paymentTermsType)
  const [prepay, setPrepay] = useState(String(order.prepaymentPercent))
  const [days, setDays] = useState(order.deferralDays != null ? String(order.deferralDays) : '')

  const isPrepayment = type === 'PREPAYMENT'
  const isDeferred = type === 'DEFERRED' || type === 'POSTPAYMENT'
  const percent = isPrepayment ? Math.min(100, Math.max(0, Number(prepay || 0))) : 0
  const approval = needsRopApproval(percent)

  const save = useMutation({
    mutationFn: () =>
      updatePaymentTerms(order.id, {
        paymentTermsType: type,
        prepaymentPercent: percent,
        deferralDays: isDeferred && days.trim() !== '' ? Number(days) : null,
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['orders'] })
      toast.success(
        updated.status === 'PENDING_ROP'
          ? 'Условия сохранены — заказ ушёл на согласование РОП'
          : 'Условия оплаты сохранены',
      )
      onClose()
    },
    onError: (e) => toast.error(apiErrorText(e, 'Не удалось сохранить условия оплаты')),
  })

  const input = 'w-full rounded border border-gray-300 px-2 py-1.5 text-sm'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-label="Условия оплаты"
        className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl"
      >
        <h3 className="mb-3 text-lg font-semibold">Условия оплаты</h3>
        <label className="mb-3 block text-sm">
          <span className="mb-1 block font-medium text-gray-700">Условие оплаты</span>
          <select
            className={input}
            value={type}
            onChange={(e) => setType(e.target.value as OrderPaymentTermsT)}
          >
            {Object.values(OrderPaymentTerms).map((t) => (
              <option key={t} value={t}>
                {ORDER_PAYMENT_TERMS_LABELS_RU[t]}
              </option>
            ))}
          </select>
        </label>
        {isPrepayment && (
          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Плановая предоплата, %</span>
            <input
              className={input}
              inputMode="numeric"
              value={prepay}
              onChange={(e) => setPrepay(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
            />
          </label>
        )}
        {isDeferred && (
          <label className="mb-3 block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {type === 'DEFERRED' ? 'Отсрочка, дней' : 'Постоплата, дней'}
            </span>
            <input
              className={input}
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
            />
          </label>
        )}
        {approval && (
          <p className="mb-3 rounded-lg bg-amber-50 p-2 text-sm text-amber-800">
            Плановая предоплата меньше 50 % — заказ уйдёт на согласование РОП. Счёт клиенту можно
            выставить сразу; согласование нужно до закупки по заказу.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button className="rounded-lg px-3 py-1.5 text-sm text-gray-600" onClick={onClose}>
            Отмена
          </button>
          <button
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-60"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'Сохраняю…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
